import { useEffect, useRef, useState } from 'react';
import { HEAD_H, HEAD_PATHS, HEAD_W } from './brandHeadPaths';
import { addSnapTarget } from '../../Homepage/connect/buffy/pixelSnap';
import './BrandLockup.css';

/* BrandLockup: the Brain Buffs logo from exports/brain-buffs-logo.html
   (Sandy 2026-09-30): the pixel Buffy head, a thin divider, and "Brain" /
   "Buffs" in DM Sans 800 (already loaded via @fontsource in src/index.js).

   collapsed = true plays the export's animation: the words slide left behind
   the divider (Brain first, then Buffs), then the divider folds away, leaving
   only the head. collapsed = false reverses it (divider returns first, then
   the words). The nav collapses it on scroll.

   Pixel rule (the "melting glasses" lesson): the head is painted on a canvas
   sized in whole DEVICE pixels. When the scale is a whole number of device
   pixels per art pixel (DPR 2 at 42 px: 3), the paths paint straight and
   crisp. When it is not (DPR 1 at 42 px: 1.5), the art is painted crisp at
   the next whole scale and then resampled down once ("sharp bilinear"), so
   every art pixel keeps the same size instead of the uneven 1-or-2 px
   pattern crispEdges gives. A whole scale within 15% of the asked size wins
   (DPR 1.25: 2 device px per art px, 44.8 px tall, fully crisp). The lockup
   is also nudged (under one device pixel) onto the device-pixel grid, e.g.
   inside the centered scrolled nav island at an odd width.
   size = the lockup height in CSS px. */
function useDevicePixelRatio() {
  const [dpr, setDpr] = useState(() => (typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1));
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(resolution: ${dpr}dppx)`);
    const onChange = () => setDpr(window.devicePixelRatio || 1);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [dpr]);
  return dpr;
}

let pathCache = null;
const headPaths = () => {
  if (!pathCache) pathCache = HEAD_PATHS.map((p) => ({ fill: p.fill, path: new Path2D(p.d) }));
  return pathCache;
};

function paintHead(canvas, devW, devH) {
  const scale = devH / HEAD_H;
  const whole = Math.abs(scale - Math.round(scale)) < 0.01;
  const k = whole ? Math.round(scale) : Math.ceil(scale);
  // crisp art at a whole scale (on its own canvas when a resample follows)
  const art = whole ? canvas : document.createElement('canvas');
  art.width = HEAD_W * k;
  art.height = HEAD_H * k;
  const a = art.getContext('2d');
  if (!a) return;
  a.setTransform(k, 0, 0, k, 0, 0);
  headPaths().forEach(({ fill, path }) => { a.fillStyle = fill; a.fill(path); });
  if (whole) return;
  canvas.width = devW;
  canvas.height = devH;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(art, 0, 0, devW, devH);
}

// headOnly: just the pixel head, no divider or words (the collapsed dashboard
// rails and the student phone bar, 2026-10-13).
export default function BrandLockup({ collapsed = false, size = 42, tone = 'light', className = '', headOnly = false }) {
  const dpr = useDevicePixelRatio();
  const canvasRef = useRef(null);
  const rootRef = useRef(null);
  // whole device pixels for the head box; CSS sizes follow from them.
  // The nearest whole art scale wins when it lands within 15% of the asked
  // size (ties go to the smaller one), so zoomed Retina (1.8 / 2.2 / 2.4),
  // 1.5x laptops and 3x phones paint crisp instead of resampled (Sandy
  // 2026-10-13: the head read blurry). Only ~1x screens still resample.
  const target = size * dpr;
  const lo = Math.max(1, Math.floor(target / HEAD_H));
  const wholeScale = Math.abs((lo + 1) * HEAD_H - target) < Math.abs(lo * HEAD_H - target) ? lo + 1 : lo;
  const devH = Math.abs(wholeScale * HEAD_H - target) <= target * 0.15
    ? wholeScale * HEAD_H
    : Math.max(HEAD_H, Math.round(target));
  const devW = Math.round((devH * HEAD_W) / HEAD_H);

  useEffect(() => {
    if (canvasRef.current) paintHead(canvasRef.current, devW, devH);
  }, [devW, devH]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    let off = [0, 0];
    return addSnapTarget({
      origin: () => {
        const b = el.getBoundingClientRect();
        return { x: b.x - off[0], y: b.y - off[1] };
      },
      apply: (x, y) => {
        off = [x, y];
        el.style.transform = x || y ? `translate(${x}px, ${y}px)` : '';
      },
    });
  }, []);

  return (
    <span
      ref={rootRef}
      className={`bb-lockup ${className}`}
      data-collapsed={collapsed ? 'true' : 'false'}
      data-tone={tone}
      style={{ '--bb-size': `${devH / dpr}px`, '--bb-head-w': `${devW / dpr}px`, '--bb-head-h': `${devH / dpr}px` }}
      role="img"
      aria-label="Brain Buffs"
    >
      <canvas ref={canvasRef} className="bb-head" width={devW} height={devH} aria-hidden="true" />
      {!headOnly && (
        <>
          <span className="bb-divider" aria-hidden="true" />
          <span className="bb-mask" aria-hidden="true">
            <span className="bb-word bb-brain">Brain</span>
            <span className="bb-word bb-buffs">Buffs</span>
          </span>
        </>
      )}
    </span>
  );
}
